import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { Tag, Tooltip } from 'antd';

import type { ResponsiveTableColumns } from '@src/components/Table';
import { COLUMN_TYPES, RESPONSIVE_COLUMN_TYPE } from '@src/components/Table/constants';
import {
  PATHS,
  PATHS_ACCORDING_TO_TYPE,
  SHIFT_STATUS,
  SHIFT_TYPES,
  SHIFT_TYPES_OPTIONS,
} from '@src/packages/shifts/constants';
import type { ShiftListItem } from '@src/packages/shifts/types';
import { DateUtil } from '@src/packages/utils/dates';

import ShiftActions from '../components/ShiftActions';
import { useShiftContext } from '../context';

export const useShiftsColumns = () => {
  const history = useHistory();
  const intl = useIntl();
  const { handleDelete } = useShiftContext();

  const handleEdit = useCallback(
    (shift: ShiftListItem) => {
      const path = PATHS_ACCORDING_TO_TYPE[shift.type as keyof typeof PATHS_ACCORDING_TO_TYPE];
      if (path) {
        history.push(`${path}/${shift.id}`);
      }
    },
    [history],
  );

  const handleAddShift = useCallback(() => history.push(PATHS.SHIFT_OPTIONS), [history]);

  const columns = useMemo(
    (): ResponsiveTableColumns<ShiftListItem> => [
      {
        title: intl.formatMessage({ id: 'shifts.table.name' }),
        dataIndex: 'name',
        key: 'name',
        align: 'left' as const,
        width: 200,
        sortable: true,
        sortConfig: { type: COLUMN_TYPES.STRING },
        render: record => <>{record.name}</>,
      },
      {
        title: intl.formatMessage({ id: 'shifts.table.exceptional' }),
        dataIndex: 'exceptional',
        key: 'exceptional',
        align: 'left' as const,
        render: record =>
          intl.formatMessage({ id: record.exceptional ? 'button.yes' : 'button.no' }),
        width: 130,
      },
      {
        title: intl.formatMessage({ id: 'shifts.table.workdayType' }),
        dataIndex: 'type',
        key: 'type',
        align: 'left' as const,
        sortable: true,
        sortConfig: { type: COLUMN_TYPES.STRING },
        render: record => {
          const typeLabels: Record<string, string> = {
            [SHIFT_TYPES.FIXED]: intl.formatMessage({ id: 'shifts.type.fixed' }),
            [SHIFT_TYPES.BIWEEKLY]: intl.formatMessage({ id: 'shifts.type.biweekly' }),
            [SHIFT_TYPES_OPTIONS.EXCEPTIONAL]: intl.formatMessage({
              id: 'shifts.type.exceptional',
            }),
            [SHIFT_TYPES.PART_TIME]: intl.formatMessage({ id: 'shifts.type.partial' }),
            [SHIFT_TYPES.SHIFT_WORK]: intl.formatMessage({ id: 'shifts.type.shiftWork' }),
            [SHIFT_TYPES.CYCLE]: intl.formatMessage({ id: 'shifts.type.cycle' }),
          };
          return typeLabels[record.type as string] || record.type;
        },
        width: 180,
      },
      {
        title: intl.formatMessage({ id: 'shifts.table.daysAndSchedule' }),
        dataIndex: 'regulationName',
        key: 'regulationName',
        align: 'left' as const,
        width: 300,
        render: record => <>{record.regulationName}</>,
      },
      {
        title: intl.formatMessage({ id: 'shifts.table.status' }),
        dataIndex: 'status',
        key: 'status',
        align: 'left' as const,
        sortable: true,
        width: 120,
        sortConfig: { type: COLUMN_TYPES.STRING },
        render: record => {
          const isPending = record.status === SHIFT_STATUS.PENDING;
          const tag = (
            <Tag color={isPending ? 'warning' : 'success'} className="gx-mb-0 gx-mr-0">
              {intl.formatMessage({
                id: isPending ? 'shifts.table.status.pending' : 'shifts.table.status.active',
              })}
            </Tag>
          );

          if (!isPending) {
            return tag;
          }

          return (
            <Tooltip title={intl.formatMessage({ id: 'shifts.status.pending.tooltip' })}>
              {tag}
            </Tooltip>
          );
        },
      },
      {
        title: intl.formatMessage({ id: 'general.updatedAt' }),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        align: 'left' as const,
        sortable: true,
        width: 150,
        sortConfig: { type: COLUMN_TYPES.DATE },
        render: record => (
          <>{DateUtil.convertTimestampToDate(record.updatedAt, 'DD/MM/YYYY HH:mm')}</>
        ),
      },
      {
        title: '',
        key: 'actions',
        align: 'left' as const,
        width: 100,
        columnType: RESPONSIVE_COLUMN_TYPE.ACTIONS,
        actionsLayout: 'between',
        render: record => (
          <ShiftActions record={record} onEdit={handleEdit} onDelete={handleDelete} />
        ),
      },
    ],
    [intl, handleEdit, handleDelete],
  );

  return {
    columns,
    handleAddShift,
  };
};
